import { SelectOption } from '@anm/components/select/Select';
import { checkCanDownload, Configs } from '@anm/hosting-player';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import * as semrush from '@anm/helpers/semrush';
import { CurrentValue } from '..';
import { hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { userSelectors } from '../../../../store/modules/user';
import ControlGroup from '../ControlGroup';

import controlGroupsData from './controlGroupsData';
import { getControlValue } from './helpers';

export type ControlType = 'checkbox' | 'color' | 'select';

export type ControlsList =
  | 'autoplay'
  | 'end'
  | 'bigPlayButton'
  | 'smallPlayButton'
  | 'speed'
  | 'fullScreen'
  | 'volume'
  | 'quality'
  | 'playerColor'
  | 'currentTime'
  | 'playbarButton'
  | 'logo'
  | 'muted'
  | 'hideUnmute'
  | 'download'
  | 'playHidden';

export type RequiredControlProps = {
  id: ControlsList;
  type: ControlType;
};
export type FieldDirection = 'horizontal' | 'vertical';

export type Controls = RequiredControlProps & {
  label?: string;
  values?: SelectOption[];
  disabled?: boolean;
  controlValue?: string | boolean;
};

type ControlPanelProps = {
  handleChange: (value: CurrentValue) => void;
  initialConfigs: Configs;
};

const ControlsPanel: FC<ControlPanelProps> = ({ handleChange, initialConfigs }) => {
  const settings = useSelector(userSelectors.selectUserHostingSettings);
  const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const sources = useSelector(hostedVideoSelectors.selectVideoSources);

  const canDownload = checkCanDownload(sources);

  const getDisabledStatus = (id: ControlsList) => {
    switch (id) {
      case 'logo':
        return !settings?.canRemoveLogo;

      case 'end':
        return !settings?.canChangeVideoEnd;

      case 'download':
        return isUserFree;

      default:
        return false;
    }
  };

  const panel = controlGroupsData.map(group => {
    const { flow, subtitle, controls, size } = group;

    const semrushCleanedControls = semrush.isSemrushIframe() ? controls.filter(c => c.id !== 'logo') : controls;
    const filteredControls = !canDownload
      ? semrushCleanedControls.filter(c => c.id !== 'download')
      : semrushCleanedControls;

    const withControlValue = filteredControls.map(control => {
      const { id, type } = control;

      return {
        ...control,
        disabled: getDisabledStatus(id),
        controlValue: getControlValue(id, type, initialConfigs)
      };
    });

    return (
      <ControlGroup
        flow={flow}
        size={size}
        key={subtitle}
        subtitle={subtitle}
        onChange={handleChange}
        controls={withControlValue}
      />
    );
  });

  return <>{panel}</>;
};

export default ControlsPanel;
